import {Component, OnDestroy, OnInit} from '@angular/core'
import {ApprovalsService} from '../services/approvals.service'
import {GetPipelinesResponse} from '../model/approval-responses'
import {StatusService} from '../services/status.service'
import {Approved, Change, Deploying, PendingApproval} from '../common/domain/approvals'
import {ApprovalDialogComponent, ApprovalDialogData} from '../dialogs/approval-dialog.component'
import {combineLatest, of, Subject} from 'rxjs'
import {distinctUntilChanged, switchMap, takeUntil} from 'rxjs/operators'
import {MatDialog} from '@angular/material/dialog'
import {ToastrService} from 'ngx-toastr'
import {ConfigVersionService} from '../services/config-version.service'

const deepEqual = require('deep-equal')

interface PendingChanges extends PendingApproval {
  changes: Change[]
}

interface StageDefinition {
  name: string
  pending: PendingChanges[]
  pendingCore?: PendingChanges[]
  pendingInfra?: PendingChanges[]
  pendingCode?: PendingChanges[]
  pendingConfig?: PendingChanges[]
  pendingTemplates?: PendingChanges[]
  deploying: Deploying[]
  changes: string[]
  requireApproval: boolean
  canApproveCore: boolean
  canApproveConfig: boolean
  canApproveTemplates: boolean
  canApproveInfra: boolean
  canApproveCode: boolean
  canApproveAll: boolean
  statusMessage: string
}

const configPipelines = [
  'config-pipeline',
  'lambda-ingest-product-pipeline',
  // 'api-publish-pipeline',
]
const codePipelines = [
  'lambda-analyse-utils-pipeline',
  'lambda-transform-utils-pipeline',
  'lambda-validate-utils-pipeline',
  // 'lambda-transform-json-utils-pipeline',
  // 'lambda-validate-json-utils-pipeline',
  'raise-jira-ticket-pipeline',
  'slack-notifications-pipeline',
  'api-health-check-pipeline',
  // 'regxchange-publisher-pipeline',
  // 'wmdaten-publisher-pipeline'
]
const templatePipelines = [
  'api-smartdx-service-pipeline',
  'lambda-anti-freeze-pipeline',
  'auto-deploy-templates-pipeline',
]
const infraPipelines = [
  'api-oauth-server-pipeline',
  'smartdx-appliance-pipeline',
  'edge-proxy-pipeline',
]
const coreInfraPipelines = [
  'bootstrap-projectinfrastructure-pipeline',
]

@Component({
  selector: 'app-approvals',
  templateUrl: './approvals.component.html',
  styleUrls: ['./approvals.component.scss'],
})
export class ApprovalsComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject()

  // toolsStage: StageDefinition = {
  //   name: 'Development',
  //   pending: [],
  //   deploying: [],
  //   changes: [],
  //   requireApproval: false,
  //   canApproveAll: false,
  //   canApproveCore: false,
  //   canApproveConfig: false,
  //   canApproveTemplates: false,
  //   canApproveInfra: false,
  //   canApproveCode: false,
  //   statusMessage: '',
  // }

  devStage: StageDefinition = {
    name: 'Dev/Staging',
    pending: [],
    deploying: [],
    changes: [],
    requireApproval: false,
    canApproveAll: false,
    canApproveCore: false,
    canApproveConfig: false,
    canApproveTemplates: false,
    canApproveInfra: false,
    canApproveCode: false,
    statusMessage: '',
  }

  uatStage: StageDefinition = {
    name: 'UAT',
    pending: [],
    deploying: [],
    changes: [],
    requireApproval: true,
    canApproveAll: false,
    canApproveCore: false,
    canApproveConfig: false,
    canApproveTemplates: false,
    canApproveInfra: false,
    canApproveCode: false,
    statusMessage: '',
  }

  prodStage: StageDefinition = {
    name: 'Production',
    pending: [],
    deploying: [],
    changes: [],
    requireApproval: true,
    canApproveAll: false,
    canApproveCore: false,
    canApproveConfig: false,
    canApproveTemplates: false,
    canApproveInfra: false,
    canApproveCode: false,
    statusMessage: '',
  }

  stages: StageDefinition[] = [
    // this.toolsStage,
    this.devStage,
    this.uatStage,
    this.prodStage,
  ]

  timeout: number
  pipelines: GetPipelinesResponse

  constructor(
    private approvalsService: ApprovalsService,
    private statusService: StatusService,
    private configVersionService: ConfigVersionService,
    private dialog: MatDialog,
    private toast: ToastrService,
  ) {
  }

  ngOnInit() {
    combineLatest([
      this.approvalsService.getPipelines(),
      this.approvalsService.getDeploying(),
      this.approvalsService.getPendingApprovals(),
      this.approvalsService.getLatestSuccessfulApprovals(),
    ])
      .pipe(
        // tap(x => {
        //   console.dir(x)
        // }),
        switchMap(
          ([
             pipelines,
             deploying,
             pendingApprovals,
             latestApprovals,
           ]) => {
            const approved = latestApprovals.approved

            // const changes = pendingApprovals.pending.reduce((list, pendingApproval) => {
            //   const previousSuccessful = approved.find(x => {
            //     return x.pipelineName === pendingApproval.pipelineName
            //       && x.pipelineApprovalStage === pendingApproval.pipelineApprovalStage
            //       && x.pipelineApprovalAction === pendingApproval.pipelineApprovalAction
            //   })
            //   const previousExecutionId = previousSuccessful && previousSuccessful.pipelineExecutionId || '-'
            //   list.push({
            //     pipelineName: pendingApproval.pipelineName,
            //     previousExecutionId: previousExecutionId,
            //     approvalExecutionId: pendingApproval.pipelineExecutionId,
            //   })
            //   return list
            // }, [])
            return of({
              pending: pendingApprovals.pending.map(p => {
                return Object.assign({changes: []}, p)
              }),
              // changes: changes,
              pipelines: pipelines,
              deploying: deploying.deploying,
              approved: approved,
            })
          },
        ),
        distinctUntilChanged((x, y) => deepEqual(x, y)),
        takeUntil(this.onDestroy$),
      )
      .subscribe((data?: {
        pipelines: GetPipelinesResponse
        approved: Approved[]
        pending: PendingChanges[]
        deploying: Deploying[]
      }) => {
        this.refreshApprovals(data.pipelines, data.deploying, data.pending, data.approved)
      })
  }

  ngOnDestroy() {
    this.onDestroy$.next()
  }

  private async refreshApprovals(
    pipelines: GetPipelinesResponse,
    deploying: Deploying[],
    pending: PendingChanges[],
    approved: Approved[],
  ) {
    this.pipelines = pipelines
    // this.toolsStage.statusMessage = ''
    this.devStage.statusMessage = ''
    this.uatStage.statusMessage = ''
    this.prodStage.statusMessage = ''

    const xxDeploying = [].concat(...deploying)
    const xxPending = [].concat(...pending)
    const statusId = this.statusService.start('Loading Change Sets')
    for (const pendingApproval of pending) {
      const previousSuccessful = approved.find(x => {
        return x.pipelineName === pendingApproval.pipelineName
          && x.pipelineApprovalStage === pendingApproval.pipelineApprovalStage
          && x.pipelineApprovalAction === pendingApproval.pipelineApprovalAction
      })
      const previousExecutionId = previousSuccessful && previousSuccessful.pipelineExecutionId || '-'
      const changes = await this.approvalsService.getChanges(pendingApproval.pipelineName, previousExecutionId, pendingApproval.pipelineExecutionId)
        .toPromise()
      pendingApproval.changes = changes.changes
    }
    for (const approvalPipeline of pipelines.approvals.filter(p => p.pipelineName !== 'api-oauth-server-pipeline')) {
      const pipelineName = approvalPipeline.pipelineName
      const latestExecution = pipelines.latestExecutions.find(p => p.pipelineName === pipelineName).pipelineExecutionId
      const latestUatSuccessfulAction = approved.find(a => {
        return a.pipelineName === pipelineName
          && a.pipelineApprovalStage.toLowerCase().endsWith('uat')
      })
      const pendingUatExecutionAction = pending.find(a => {
        return a.pipelineName === pipelineName
          && a.pipelineApprovalStage.toLowerCase().endsWith('uat')
      })
      const latestUatExecution = pendingUatExecutionAction
        ? pendingUatExecutionAction.pipelineExecutionId
        : latestUatSuccessfulAction
          ? latestUatSuccessfulAction.pipelineExecutionId
          : '-'
      if (latestExecution !== latestUatExecution) {
        const changes = await this.approvalsService.getChanges(pipelineName, latestUatExecution, latestExecution)
          .toPromise()
        xxPending.push({
          pipelineName: pipelineName,
          pipelineApprovalStage: 'DeployToDev',
          pipelineApprovalAction: 'N/A',
          pipelineExecutionId: latestExecution,
          pipelineApprovalToken: 'N/A',
          changes: changes.changes,
        })
        xxDeploying.push({
          pipelineName: pipelineName,
          pipelineApprovalStage: 'DeployToDev',
          pipelineExecutionId: latestExecution,
        })
      }
    }
    this.statusService.complete(statusId)

    this.devStage.pending = xxPending.filter(x => x.pipelineApprovalStage === 'DeployToDev')
    this.devStage.deploying = xxDeploying.filter(x => x.pipelineApprovalStage === 'DeployToDev')
    this.devStage.canApproveAll = this.devStage.pending.length > 0 && this.devStage.deploying.length === 0
    this.devStage.canApproveCore = this.devStage.pending.length > 0 && this.devStage.deploying.length === 0
    this.devStage.canApproveConfig = this.devStage.pending.length > 0 && (this.devStage.deploying.length === 0 || !this.devStage.deploying.some(d => configPipelines.includes(d.pipelineName)))
    this.devStage.canApproveTemplates = this.devStage.pending.length > 0 && (this.devStage.deploying.length === 0 || !this.devStage.deploying.some(d => templatePipelines.includes(d.pipelineName)))
    this.devStage.canApproveInfra = this.devStage.pending.length > 0 && (this.devStage.deploying.length === 0 || !this.devStage.deploying.some(d => infraPipelines.includes(d.pipelineName)))
    this.devStage.canApproveCode = this.devStage.pending.length > 0 && (this.devStage.deploying.length === 0 || !this.devStage.deploying.some(d => codePipelines.includes(d.pipelineName)))
    this.devStage.changes = this.devStage.pending.reduce((list: string[], current) => {
      current.changes.forEach(change => {
        const exists = list.includes(change.repositoryRevisionSummary)
        if (!exists) {
          list.push(change.repositoryRevisionSummary)
        }
      })
      return list
    }, [])

    this.uatStage.pending = xxPending.filter(x => x.pipelineApprovalStage === 'DeployToUat')
    this.uatStage.deploying = xxDeploying.filter(x => x.pipelineApprovalStage === 'DeployToUat')
    this.uatStage.canApproveAll = this.uatStage.pending.length > 0 && this.uatStage.deploying.length === 0
    this.uatStage.canApproveCore = this.uatStage.pending.length > 0 && this.uatStage.deploying.length === 0
    this.uatStage.canApproveConfig = this.uatStage.pending.length > 0 && (this.uatStage.deploying.length === 0 || !this.uatStage.deploying.some(d => configPipelines.includes(d.pipelineName)))
    this.uatStage.canApproveTemplates = this.uatStage.pending.length > 0 && (this.uatStage.deploying.length === 0 || !this.uatStage.deploying.some(d => templatePipelines.includes(d.pipelineName)))
    this.uatStage.canApproveInfra = this.uatStage.pending.length > 0 && (this.uatStage.deploying.length === 0 || !this.uatStage.deploying.some(d => infraPipelines.includes(d.pipelineName)))
    this.uatStage.canApproveCode = this.uatStage.pending.length > 0 && (this.uatStage.deploying.length === 0 || !this.uatStage.deploying.some(d => codePipelines.includes(d.pipelineName)))
    this.uatStage.changes = this.uatStage.pending.reduce((list: string[], current) => {
      current.changes.forEach(change => {
        const exists = list.includes(change.repositoryRevisionSummary)
        if (!exists) {
          list.push(change.repositoryRevisionSummary)
        }
      })
      return list
    }, [])

    this.prodStage.pending = xxPending.filter(x => x.pipelineApprovalStage === 'DeployToProd')
    this.prodStage.deploying = xxDeploying.filter(x => x.pipelineApprovalStage === 'DeployToProd')
    this.prodStage.canApproveAll = this.prodStage.pending.length > 0 && this.prodStage.deploying.length === 0
    this.prodStage.canApproveCore = this.prodStage.pending.length > 0 && this.prodStage.deploying.length === 0
    this.prodStage.canApproveConfig = this.prodStage.pending.length > 0 && (this.prodStage.deploying.length === 0 || !this.prodStage.deploying.some(d => configPipelines.includes(d.pipelineName)))
    this.prodStage.canApproveTemplates = this.prodStage.pending.length > 0 && (this.prodStage.deploying.length === 0 || !this.prodStage.deploying.some(d => templatePipelines.includes(d.pipelineName)))
    this.prodStage.canApproveInfra = this.prodStage.pending.length > 0 && (this.prodStage.deploying.length === 0 || !this.prodStage.deploying.some(d => infraPipelines.includes(d.pipelineName)))
    this.prodStage.canApproveCode = this.prodStage.pending.length > 0 && (this.prodStage.deploying.length === 0 || !this.prodStage.deploying.some(d => codePipelines.includes(d.pipelineName)))
    this.prodStage.changes = this.prodStage.pending.reduce((list: string[], current) => {
      current.changes?.forEach(change => {
        const exists = list.includes(change.repositoryRevisionSummary)
        if (!exists) {
          list.push(change.repositoryRevisionSummary)
        }
      })
      return list
    }, [])

    if (this.devStage.deploying.length > 0) {
      this.devStage.canApproveAll = false
      this.devStage.statusMessage = 'Deployment in progress'
      this.uatStage.canApproveAll = false
      this.uatStage.statusMessage = 'Staging is being updated'
      if (this.devStage.deploying.some(d => configPipelines.includes(d.pipelineName))) {
        this.uatStage.canApproveConfig = false
      }
      if (this.devStage.deploying.some(d => coreInfraPipelines.includes(d.pipelineName))) {
        this.uatStage.canApproveCore = false
      }
      if (this.devStage.deploying.some(d => templatePipelines.includes(d.pipelineName))) {
        this.uatStage.canApproveTemplates = false
      }
      if (this.devStage.deploying.some(d => infraPipelines.includes(d.pipelineName))) {
        this.uatStage.canApproveInfra = false
      }
      if (this.devStage.deploying.some(d => codePipelines.includes(d.pipelineName))) {
        this.uatStage.canApproveCode = false
      }
    }
    if (this.uatStage.deploying.length > 0) {
      this.uatStage.canApproveAll = false
      this.uatStage.statusMessage = 'Deployment in progress'
      this.prodStage.canApproveAll = false
      this.prodStage.statusMessage = 'UAT is being updated'
      if (this.uatStage.deploying.some(d => configPipelines.includes(d.pipelineName))) {
        this.prodStage.canApproveConfig = false
      }
      if (this.uatStage.deploying.some(d => coreInfraPipelines.includes(d.pipelineName))) {
        this.prodStage.canApproveCore = false
      }
      if (this.uatStage.deploying.some(d => templatePipelines.includes(d.pipelineName))) {
        this.prodStage.canApproveTemplates = false
      }
      if (this.uatStage.deploying.some(d => infraPipelines.includes(d.pipelineName))) {
        this.prodStage.canApproveInfra = false
      }
      if (this.uatStage.deploying.some(d => codePipelines.includes(d.pipelineName))) {
        this.prodStage.canApproveCode = false
      }
    }
    if (this.prodStage.deploying.length > 0) {
      this.prodStage.canApproveInfra = false
      this.prodStage.canApproveCore = false
      this.prodStage.canApproveAll = false
      this.prodStage.canApproveCode = false
      this.prodStage.canApproveConfig = false
      this.prodStage.canApproveTemplates = false
      this.prodStage.statusMessage = 'Deployment in progress'
    }

    for (const stage of this.stages) {
      stage.pendingCore = stage.pending.filter(p => coreInfraPipelines.includes(p.pipelineName))
      stage.pendingInfra = stage.pending.filter(p => infraPipelines.includes(p.pipelineName))
      stage.pendingConfig = stage.pending.filter(p => configPipelines.includes(p.pipelineName))
      stage.pendingCode = stage.pending.filter(p => codePipelines.includes(p.pipelineName))
      stage.pendingTemplates = stage.pending.filter(p => templatePipelines.includes(p.pipelineName))
      if (stage.pendingInfra.length > 0 || stage.pendingCore.length > 0) {
        stage.canApproveAll = false
      }
    }
  }

  approveChanges(pending: PendingChanges[]) {
    const dialogRef = this.dialog.open(ApprovalDialogComponent, {
      width: '500px',
      data: {},
      panelClass: 'dialogFormField500',
    })
    dialogRef.afterClosed().subscribe(async (data: ApprovalDialogData) => {
      if (data && data.jira && data.comment) {
        this.approvalsService.approve(pending, data.jira, data.comment)
          .subscribe(res => {
            this.toast.success('Approved...')
          })
      }
    })
  }
}
